import { sendMsgOnReloaded } from '@/utils/msgOnReload';
import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

/**
 * Toast 层级
 *
 * 必须压过所有遮罩层：抽屉 / 弹窗 / 嵌套弹窗统一是 `z-50`，个别业务浮层用到 `z-999`。
 * 这里取一个远大于它们、且小于 32 位有符号整数上限的值，后续再新增浮层也不用回来改这里。
 */
const TOASTER_Z_INDEX = 2_147_483_000;

/**
 * Toast 容器样式
 *
 * 除了主题变量，另外两条是「遮罩层打开时 toast 仍可用」的关键：
 *
 * 1. `pointerEvents: 'all'` —— 抽屉 / 弹窗打开时，Radix 的滚动锁会把 `body` 置为
 *    `pointer-events: none`，只给 overlay / content 单独放行。toast 挂在 `body` 下会继承
 *    `none`，于是复制、hover 暂停、上滑移除全部失效，点击还会穿透到遮罩上。
 *    这里显式放行；容器自身高度为 0，不会挡住 toast 之外的区域。
 * 2. `zIndex` —— 见上方常量说明。
 */
const TOASTER_STYLE = {
  '--normal-bg': 'var(--popover)',
  '--normal-text': 'var(--popover-foreground)',
  '--normal-border': 'var(--border)',
  '--border-radius': 'var(--radius)',
  zIndex: TOASTER_Z_INDEX,
  pointerEvents: 'all',
} as React.CSSProperties;

/**
 * 阻断事件冒泡到 document
 *
 * 遮罩层（Radix DismissableLayer）在 `document` 上监听 `pointerdown` / `focusin`，
 * 只要事件目标不在弹层内就判定为「点了外部」，直接关掉抽屉或弹窗 —— 用户只是点了
 * toast 上的「复制」，整个抽屉连同填了一半的表单就没了。所以 toast 上的指针与焦点
 * 事件必须止步于此，不能让它冒到 `document`。
 *
 * 注意：必须用「冒泡阶段」而不是捕获阶段，否则会先于 toast 内部的按钮点击、拖拽关闭
 * 等处理执行，把它们一起挡掉。
 */
const stopPropagation = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

const Toaster = ({ ...props }: ToasterProps) => {
  const { themeModel } = useTheme();

  useEffect(() => {
    sendMsgOnReloaded();
  }, []);

  return (
    <div data-slot="toaster-host" onPointerDown={stopPropagation} onFocus={stopPropagation}>
      <Sonner
        theme={themeModel as ToasterProps['theme']}
        icons={{
          success: <CircleCheckIcon className="size-4" />,
          info: <InfoIcon className="size-4" />,
          warning: <TriangleAlertIcon className="size-4" />,
          error: <OctagonXIcon className="size-4" />,
          loading: <Loader2Icon className="size-4 animate-spin" />,
        }}
        {...props}
        className={cn('toaster group', props.className)}
        style={{ ...TOASTER_STYLE, ...props.style }}
      />
    </div>
  );
};

export { Toaster };
